import { Button, Form, Modal, Select, Space } from 'antd';
import { useGetSeasonsForFieldImportQuery } from 'app/api';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useFieldSlice } from '../../slice';
import { selectImportFieldsFromSeasonState } from '../../slice/selectors';

interface IProps {}

const IMPORT_FROM_SEASON_FORM_ID = 'importFieldsFromSeasonFormId';

const ImportFieldsFromSeasonModal: FC<IProps> = props => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const { actions } = useFieldSlice();

  const { isOpen, loading } = useSelector(selectImportFieldsFromSeasonState);
  const envId = useSelector(selectSelectedEnvId);
  const selectedSeasonId = useSelector(selectSelectedSeasonId);
  const companyId = useSelector(selectSelectedCompanyId);

  const {
    data: seasonsForFieldImport,
    isFetching,
    refetch: refetchSeasonsForFieldImport,
  } = useGetSeasonsForFieldImportQuery(
    { companyId: companyId as number, envId: envId as number },
    { skip: !companyId || !envId || !isOpen },
  );

  useEffect(() => {
    if (!isOpen && form) {
      form.resetFields();
    }
    if (isOpen) {
      refetchSeasonsForFieldImport();
    }
  }, [form, isOpen, refetchSeasonsForFieldImport]);

  const onSubmit = ({ season }: { season: number }) => {
    if (envId && companyId && selectedSeasonId && !isFetching) {
      dispatch(
        actions.submitImportFieldsFromSeason({
          envId,
          seasonId: selectedSeasonId,
          fromSeasonId: season,
          companyId,
        }),
      );
    }
  };

  const onCancel = () => {
    dispatch(actions.toggleImportFieldsFromSeasonModal(false));
  };

  return (
    <Modal
      title={t(
        'Field.ImportFromSeason.ImportFieldsFromAnotherSeason',
        'Import fields from another season',
      )}
      centered
      visible={isOpen}
      footer={
        <Space>
          <Button onClick={onCancel}>
            {t('Field.ImportFromSeason.Cancel', 'Cancel')}
          </Button>
          <Button
            type="primary"
            loading={loading}
            form={IMPORT_FROM_SEASON_FORM_ID}
            key="submit"
            htmlType="submit"
          >
            {t('Field.ImportFromSeason.Import', 'Import')}
          </Button>
        </Space>
      }
      onCancel={onCancel}
    >
      <Form
        name="importFieldsFromSeasonForm"
        form={form}
        id={IMPORT_FROM_SEASON_FORM_ID}
        onFinish={onSubmit}
        requiredMark={false}
        layout="vertical"
      >
        <Form.Item
          label={t(
            'Field.ImportFromSeason.SelectFromWhichSeason',
            'Select from which season you want to import the fields',
          )}
          name={'season'}
          rules={[{ required: true }]}
        >
          <Select
            loading={isFetching}
            dropdownMatchSelectWidth={false}
            placeholder={t(
              'Field.ImportFromSeason.SelectSeason',
              'Select season',
            )}
          >
            {seasonsForFieldImport?.map(({ season, fieldCount }) => (
              <Select.Option
                key={season.id}
                value={season.id}
                disabled={selectedSeasonId === season.id || !fieldCount}
              >
                {`${season.year} (${fieldCount})`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ImportFieldsFromSeasonModal;
